import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, autofill, getFormValues, change } from 'redux-form';
import { trackEvent } from 'user-analytics';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { updatePurchaserData } from '@/actions/siemprePlus';
import { fieldRenderer, FilterableSelectRenderer } from 'utils/formRenderers';
import { Spacing, Text } from '@reservamos/elements';
import DocumentTypeField from 'components/DocumentTypeField';
import getDocTypeRegex from 'utils/getDocTypeRegex';
import normalizeRegex from 'utils/normalizeRegex';
import { documentId, required } from 'utils/formValidations';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useBillingCompanyLookup, { BILLING_COMPANY_STATUS } from 'hooks/useBillingCompanyLookup';
import {
  companyCreatedTracker,
  companySelectionClearedTracker,
} from 'metrics/user-analytics/purchase';
import { getDocumentTypeInstructioni18NKey } from 'utils/documentMaps';
import {
  getEnabledBillingCompanyDocuments,
  isBillingCompanyDocument,
  isBillingCompanyDocumentOrLegacy,
} from 'utils/purchase/billingCompanyDocuments';
import { isThirdPartyRecipient } from 'utils/purchase/billingDocumentValidation';
import { useReserhubAccount } from 'context/ReserhubAccount';
import { setStoredPurchaser } from 'utils/storedPurchaser';
import normalizeName from '../../../utils/normalizeName';
import normalizeFirstName from '../../../utils/normalizeFirstName';
import { PhoneNumberField } from '../../ReduxFields';
import LabelSwitch from '../../../ui/atoms/LabelSwitch';
import {
  FIRST_DATA_PASSENGER_SWITCH_CLICKED,
  SWITCH_VALUE_PROP,
} from '../../../constants/TrackEvents';
import usePurchase from '../../../hooks/store/usePurchase';
import useLoyaltyPrograms from '../../../loyalty/context/useLoyaltyPrograms';
import { getUserByWalletType, walletTypeNeedsUserInPurchaser } from '../../../utils/loyalty';
import parsePurchaser from '../../../utils/purchase/parsePurchaser';
import AddressFields from './AddressFields';
import { purchaserAutoComplete } from '../../../utils/fieldAutoComplete';

// Purchaser identity fields that `performAutofill` copies from the first
// passenger, mapped to the passenger key each is copied from. They are cleared
// when the buyer switches to third-party billing so the tercero is never
// invoiced with the passenger's identity. The mapping lets the switch clear only
// values that still equal the first passenger's — see the effect below.
const AUTOFILLED_IDENTITY_FIELDS = {
  purchaserFirstName: 'firstName',
  purchaserLastName: 'lastName',
  purchaserSecondLastName: 'secondLastName',
  documentType: 'documentType',
  documentId: 'documentId',
  email: 'email',
};

/** Selects the purchaser redux-form values without subscribing to the whole store. */
const selectPurchaserFormValues = (state) => getFormValues('purchaser')(state);
/** Selects the passengers redux-form values without subscribing to the whole store. */
const selectPassengersFormValues = (state) => getFormValues('passengers')(state);

/**
 * PurchaserFields component renders the form fields for the purchaser's information.
 * It handles autofill functionality, loyalty program integration, and form validation.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.purchaser - The purchaser's data.
 * @param {boolean} props.isExchange - Flag indicating if the purchase is an exchange.
 * @param {boolean} props.showPurchaserForm - Flag indicating if the purchaser form should be displayed.
 * @param {boolean} props.hideIdentityFields - Hides name, document, and address fields showing only phone and email.
 * @param {boolean} props.isQuickCheckout - Prevents billing-company lookup in QuickCheckout.
 * @param {object} props.initialBillingCompany - Serialized/stored billing-company selection.
 *
 * @returns {JSX.Element} The rendered component
 */
const PurchaserFields = ({
  purchaser,
  isExchange,
  showPurchaserForm,
  hideIdentityFields,
  isQuickCheckout,
  initialBillingCompany,
}) => {
  const { AUTOFILL_ENABLED_DEFAULT } = useWhitelabelFeatures();
  const [autofillEnabled, setAutoFillEnabled] = useState(AUTOFILL_ENABLED_DEFAULT);
  const { selectedLoyaltyProgram } = useLoyaltyPrograms();
  const purchase = usePurchase();
  const whitelabelConfig = useSelector((state) => state.whitelabelConfig);
  const purchaserFormValues = useSelector(selectPurchaserFormValues);
  const passengersFormValues = useSelector(selectPassengersFormValues);
  const { t } = useTranslation('passengers');
  const {
    features,
    copies: { others },
    env: { theme, brand },
  } = whitelabelConfig;
  const { user: reserhubUser } = useReserhubAccount();
  const phoneNumberLabel = theme.phoneNumberLabel || t('phone');
  const dispatch = useDispatch();
  const purchaserDocumentType =
    passengersFormValues?.documentType || purchaserFormValues?.documentType;
  const billingCompanyDocumentType = purchaserFormValues?.documentType;
  const billingCompanyDocument = purchaserFormValues?.documentId || '';
  // The visible name field doubles as the business name while a billable document is selected.
  const purchaserBusinessName = purchaserFormValues?.purchaserFirstName || '';
  const phoneCountry = purchaser?.phoneCountry || purchaserFormValues?.phoneCountry || '';
  const acceptBusinessName = features.ACCEPT_PURCHASER_BUSINESS_NAME;
  const showDocumentField = features.IDENTIFICATION_DOCUMENT_FOR_PURCHASER;
  // Invoicing a third party requires the buyer's address + city to create the tercero, so the
  // address section shows for that case regardless of doc type. Folded into develop's
  // `showAddressFields` below rather than declared here, so there is a single source of truth.
  const isThirdPartyBilling = isThirdPartyRecipient(purchaserFormValues?.billingRecipientType);
  const { purchaserCountries } = useWhitelabelEnvs();
  const initialFields = useRef(null);
  const purchaserFormValuesRef = useRef(purchaserFormValues);
  const manualCompanyCapture = useRef(false);
  const previousBillingCompanyIdentity = useRef('');
  purchaserFormValuesRef.current = purchaserFormValues;

  const { walletType } = purchase;
  // Tenant configuration only: which document types can be billed is a fiscal rule per country,
  // not something to roll out or experiment with.
  const allowedBillingDocuments = useMemo(
    () =>
      getEnabledBillingCompanyDocuments(
        features.BILLING_COMPANY_ENABLED,
        features.BILLING_COMPANY_AVAILABLE_DOCUMENTS,
      ),
    [features.BILLING_COMPANY_AVAILABLE_DOCUMENTS, features.BILLING_COMPANY_ENABLED],
  );
  // The flow exists only while the tenant firewall is open and the allowlist has entries; the
  // document the buyer typed decides whether it applies to this purchase.
  const billingCompanyFlowConfigured =
    allowedBillingDocuments.length > 0 && !isQuickCheckout && !hideIdentityFields;
  const isBillingDocumentType = isBillingCompanyDocument(
    billingCompanyDocumentType,
    allowedBillingDocuments,
  );
  const billingCompanyLookupEnabled = billingCompanyFlowConfigured && isBillingDocumentType;
  // Tied to the tenant, not to the document: once this form owns the company experience, the
  // purchaser's own document type drives its regex and labels for every type, billable or not.
  const effectivePurchaserDocumentType = billingCompanyFlowConfigured
    ? billingCompanyDocumentType
    : purchaserDocumentType;
  // The allowlist owns company presentation when this form runs the new flow. QuickCheckout and
  // hidden-identity variants stay on the legacy NIT behavior because they intentionally opt out.
  const usesBillingCompanyFields = isBillingCompanyDocumentOrLegacy(
    effectivePurchaserDocumentType,
    billingCompanyFlowConfigured ? allowedBillingDocuments : [],
  );
  const showPurchaserAddressFields =
    features.SHOW_PURCHASER_ADDRESS_FIELDS && usesBillingCompanyFields;
  const {
    status: billingCompanyStatus,
    company: billingCompany,
    cities: billingCompanyCities,
    normalizedDocument: normalizedBillingCompanyDocument,
  } = useBillingCompanyLookup({
    purchaseToken: purchase.token,
    document: billingCompanyDocument,
    documentType: billingCompanyDocumentType,
    enabled: billingCompanyLookupEnabled,
    initialCompanyId: initialBillingCompany?.id,
    initialCompanyName: initialBillingCompany?.name,
    initialCompanyAddress: initialBillingCompany?.address,
    initialCompanyDocument: initialBillingCompany?.document,
  });
  // Which document types can be billed is a per-tenant decision, not a hardcoded NIT: the provider
  // searches by number alone, so without the allowlist a personal document would look like a
  // company waiting to be created.
  const showBillingCompanyExperience = billingCompanyLookupEnabled;
  const normalizedBillingCompanyDocumentType = String(billingCompanyDocumentType || '')
    .trim()
    .toUpperCase();
  const billingCompanyIdentity = `${normalizedBillingCompanyDocumentType}:${normalizedBillingCompanyDocument}`;
  const billingCompanyFound =
    showBillingCompanyExperience && billingCompanyStatus === BILLING_COMPANY_STATUS.FOUND;
  const showManualBillingCompany =
    showBillingCompanyExperience &&
    (billingCompanyStatus === BILLING_COMPANY_STATUS.NOT_FOUND ||
      billingCompanyStatus === BILLING_COMPANY_STATUS.ERROR);
  // A resolved company bills against the address the provider already holds, and the API never
  // returns it, so asking the buyer for one would collect data nobody consumes.
  // Third-party invoicing is the one case that needs the address regardless of document type:
  // the provider builds the "tercero" from it, so it cannot be skipped. That path only turns on
  // with ENABLE_THIRD_PARTY_INVOICE, so the company flow above is unaffected.
  const showAddressFields =
    isThirdPartyBilling || (showPurchaserAddressFields && !billingCompanyFound);
  // The minimal layout asks only for the street + city `crearTercero` needs. The billing-company
  // flow owns the city control when it runs, so it keeps the full layout even for a third party.
  const useMinimalAddress = isThirdPartyBilling && !showBillingCompanyExperience;
  // `AddressFields` bails out without a country catalog, so the address city only exists when
  // the whole section renders. This is what decides where the single city control lives.
  const showsAddressCity =
    !hideIdentityFields && showAddressFields && !useMinimalAddress && Boolean(purchaserCountries);

  useEffect(() => {
    if (!billingCompanyFlowConfigured) return;

    dispatch(
      change('purchaser', 'billingCompanyValidationEnabled', Boolean(showBillingCompanyExperience)),
    );
  }, [billingCompanyFlowConfigured, dispatch, showBillingCompanyExperience]);

  useEffect(() => {
    if (!showPurchaserForm) return;
    if (!Object.keys(purchaser).length) return;
    if (!initialFields.current) initialFields.current = { ...purchaser };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaser]);

  useEffect(() => {
    manualCompanyCapture.current =
      billingCompanyStatus === BILLING_COMPANY_STATUS.NOT_FOUND ||
      billingCompanyStatus === BILLING_COMPANY_STATUS.ERROR;
  }, [billingCompanyStatus]);

  useEffect(() => {
    if (!purchase.billingCompanyId || !manualCompanyCapture.current) return;
    companyCreatedTracker();
    manualCompanyCapture.current = false;
  }, [purchase.billingCompanyId]);

  useEffect(() => {
    if (!billingCompanyFound || !billingCompany) return;

    const currentValues = purchaserFormValuesRef.current || {};
    const taxpayerAddress = billingCompany.address || currentValues.taxpayerAddress || '';
    dispatch(change('purchaser', 'purchaserFirstName', billingCompany.name));
    dispatch(change('purchaser', 'taxpayerLegalName', billingCompany.name));
    // The search now carries the type, so the answer normally echoes the selection back. Only a
    // genuinely different type is worth writing: re-dispatching the same value in another casing
    // would re-run the lookup and leave the select on a value it has no option for.
    const registeredDocumentType = billingCompany.documentType || '';
    const selectedDocumentType = String(currentValues.documentType || '').toUpperCase();
    if (registeredDocumentType && registeredDocumentType.toUpperCase() !== selectedDocumentType) {
      dispatch(change('purchaser', 'documentType', registeredDocumentType));
    }
    dispatch(change('purchaser', 'taxpayerAddress', taxpayerAddress));
    dispatch(change('purchaser', 'billingCompanyId', billingCompany.id));
    dispatch(change('purchaser', 'billingCompanyCityId', ''));

    setStoredPurchaser({
      ...currentValues,
      purchaserFirstName: billingCompany.name,
      taxpayerLegalName: billingCompany.name,
      taxpayerAddress,
      billingCompanyId: billingCompany.id,
    });
  }, [billingCompany, billingCompanyFound, dispatch]);

  // Manual capture serializes whatever the business name field shows, but a value that arrived with
  // the stored purchaser — or with the first-passenger autofill — never fires an input event, so
  // `handleBusinessNameChange` alone would ship an empty `taxpayerLegalName`. Mirroring when the
  // lookup enters manual capture covers those origins; it never overwrites a later edit because the
  // legal name is not editable on its own and only ever moves towards the name the buyer can see.
  // Declared before the document-change cleanup so a commit that does both ends on the cleared value.
  useEffect(() => {
    if (!showManualBillingCompany) return;

    const currentValues = purchaserFormValuesRef.current || {};
    const businessName = currentValues.purchaserFirstName || '';
    if (!businessName || businessName === currentValues.taxpayerLegalName) return;

    dispatch(change('purchaser', 'taxpayerLegalName', businessName));
  }, [dispatch, purchaserBusinessName, showManualBillingCompany]);

  // Retyping the document or changing its type swaps the complete billing identity, so either
  // change drops the previous match. `billingCompanyFound` is deliberately not consulted: on the
  // render where the identity changes it can still hold the prior company, and skipping here would
  // leave that company's name paired with a different type-and-number key.
  useEffect(() => {
    const previousIdentity = previousBillingCompanyIdentity.current;
    previousBillingCompanyIdentity.current = billingCompanyIdentity;

    if (
      !showBillingCompanyExperience ||
      !previousIdentity ||
      previousIdentity === billingCompanyIdentity
    ) {
      return;
    }

    const currentValues = purchaserFormValuesRef.current || {};

    dispatch(change('purchaser', 'taxpayerAddress', ''));
    dispatch(change('purchaser', 'purchaserFirstName', ''));
    dispatch(change('purchaser', 'taxpayerLegalName', ''));
    dispatch(change('purchaser', 'billingCompanyId', ''));
    dispatch(change('purchaser', 'billingCompanyCityId', ''));

    if (!currentValues.billingCompanyId) return;

    const storedValues = { ...currentValues };
    delete storedValues.billingCompanyId;
    setStoredPurchaser({
      ...storedValues,
      purchaserFirstName: '',
      taxpayerLegalName: '',
      taxpayerAddress: '',
    });
    companySelectionClearedTracker();
  }, [billingCompanyIdentity, dispatch, showBillingCompanyExperience]);

  // A document that cannot be billed must not carry company state into the payload: the backend
  // registers the company from what the purchaser ships, so leftovers from a previous NIT — or
  // rehydrated from the stored purchaser — would file a natural person as a company. Runs on mount
  // too, which is what catches the first-passenger autofill seeding a personal document.
  useEffect(() => {
    if (!billingCompanyFlowConfigured || isBillingDocumentType) return;

    const currentValues = purchaserFormValuesRef.current || {};
    const hasCompanyState = Boolean(
      currentValues.billingCompanyId ||
        currentValues.billingCompanyCityId ||
        currentValues.taxpayerLegalName ||
        currentValues.taxpayerAddress,
    );
    if (!hasCompanyState) return;

    // The visible field doubles as the business name, and every writer of that pair — the found
    // company, `handleBusinessNameChange`, the manual-capture mirror — sets both halves to the
    // same value. So a name still equal to the legal name is the company's and leaves with the
    // rest of the company state; one that has diverged (autofill, a buyer who retyped it) is a
    // personal name and stays. Trimmed to survive a stored value with stray whitespace.
    const mirroredCompanyName = String(currentValues.taxpayerLegalName || '').trim();
    const clearsMirroredName =
      Boolean(mirroredCompanyName) &&
      String(currentValues.purchaserFirstName || '').trim() === mirroredCompanyName;

    dispatch(change('purchaser', 'billingCompanyId', ''));
    dispatch(change('purchaser', 'billingCompanyCityId', ''));
    dispatch(change('purchaser', 'taxpayerLegalName', ''));
    dispatch(change('purchaser', 'taxpayerAddress', ''));
    if (clearsMirroredName) dispatch(change('purchaser', 'purchaserFirstName', ''));

    if (!currentValues.billingCompanyId) return;

    const storedValues = { ...currentValues };
    delete storedValues.billingCompanyId;
    // Storage is what rehydrates the next visit, so it has to lose the same name the form just
    // cleared — otherwise the company name returns to the personal field on the next purchase.
    if (clearsMirroredName) storedValues.purchaserFirstName = '';
    setStoredPurchaser({
      ...storedValues,
      taxpayerLegalName: '',
      taxpayerAddress: '',
    });
    companySelectionClearedTracker();
  }, [billingCompanyFlowConfigured, dispatch, isBillingDocumentType]);

  useEffect(() => {
    if (!isExchange && walletTypeNeedsUserInPurchaser(walletType)) {
      const user = getUserByWalletType(walletType);
      if (user) updatePurchaserData(parsePurchaser(user));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletType, selectedLoyaltyProgram]);

  // RESERHUB USER FOR PURCHASER
  useEffect(() => {
    if (!isExchange && reserhubUser) {
      updatePurchaserData(parsePurchaser(reserhubUser));
    }
  }, [reserhubUser, isExchange]);

  /**
   * Checks if the current field should be disabled
   * @param {String} fieldName - field name to check if it should be disabled
   * @returns Boolean
   */
  const isDisabled = (fieldName) => {
    /**
     * This condition is to disabled the fields when the purchase is an exchange,
     * Checks if the exchange should disable the purchaser fields,
     * Checks if the purchaser form is shown,
     * Checks if the initial values are set (only for exchanges),
     * Checks if the initial values are set for the current field, this is done to avoid the user change it,
     * If a field is not filled, the field is not disabled to be able to fill it. This can happen with terminal purchases.
     */

    const exchangeDisablingCondition =
      isExchange &&
      features.DISABLE_EXCHANGE_PURCHASER &&
      initialFields.current &&
      initialFields.current[fieldName];

    /**
     * Fields are disabled if autofill is enabled, the phone field is not disabled because it is filled in this step.
     * Remember that the autofill function fills the fields with the first passenger data
     */
    const disableBecauseAutoFill = fieldName !== 'phone' && autofillEnabled;

    /**
     * Checks if the loyalty program needs to disable the purchaser data.s
     */
    const loyaltyDisabling =
      walletTypeNeedsUserInPurchaser(selectedLoyaltyProgram) &&
      purchaserFormValues &&
      purchaserFormValues[fieldName];

    // `documentId` stays editable on purpose: retyping it is how the buyer looks up another
    // company, which replaces the button this flow used to need.
    const billingCompanyDisabling =
      billingCompanyFound && ['documentType', 'purchaserFirstName'].includes(fieldName);

    return (
      exchangeDisablingCondition ||
      disableBecauseAutoFill ||
      loyaltyDisabling ||
      billingCompanyDisabling
    );
  };

  /**
   * Autofill function to populate purchaser's fields with first passenger data.
   * @param {Object} passenger - First passenger's data.
   */
  const performAutofill = (passenger) => {
    const {
      firstName,
      lastName,
      secondLastName,
      documentType,
      documentId,
      email,
      phone,
      phoneCode,
      phoneCountry,
    } = passenger;

    if (firstName) dispatch(autofill('purchaser', 'purchaserFirstName', firstName));
    if (lastName) dispatch(autofill('purchaser', 'purchaserLastName', lastName));
    if (secondLastName) dispatch(autofill('purchaser', 'purchaserSecondLastName', secondLastName));
    if (documentType) dispatch(autofill('purchaser', 'documentType', documentType));
    if (documentId) dispatch(autofill('purchaser', 'documentId', documentId));
    if (email) dispatch(autofill('purchaser', 'email', email));
    if (phone && phoneCode && phoneCountry) {
      dispatch(autofill('purchaser', 'phone', phone));
      dispatch(autofill('purchaser', 'phoneCode', phoneCode));
      dispatch(autofill('purchaser', 'phoneCountry', phoneCountry));
    }
  };

  /**
   * Toggles the autofill feature for the purchaser's fields with the first passenger data
   */
  const toggleAutofill = (e) => {
    const { passengers } = purchase;
    // Sending analytics event when the switch is clicked
    trackEvent(FIRST_DATA_PASSENGER_SWITCH_CLICKED, {
      [SWITCH_VALUE_PROP]: e.target.checked,
    });

    if (!autofillEnabled) {
      performAutofill(passengers[0]);
    }
    setAutoFillEnabled(!autofillEnabled);
  };

  // Ejecuta autofill al inicio si `autofillEnabled` es `true`
  useEffect(() => {
    if (autofillEnabled) {
      performAutofill(purchase.passengers[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Invoicing a third party requires the buyer to differ from the passenger, so
  // "use first passenger data" must be off when billing a third party. Turning it
  // off is not enough: the fields performAutofill already copied from the first
  // passenger stay populated, so also clear them. Gate on the values, not on
  // `autofillEnabled`: if the buyer toggles autofill off *before* switching to
  // third party, the copied values remain but `autofillEnabled` is already false,
  // so the old guard skipped the clear and the tercero kept the passenger's data.
  // Clear only fields still equal to the first passenger's, leaving values the
  // buyer edited — or a tercero hydrated on reload — untouched.
  useEffect(() => {
    if (!isThirdPartyBilling) return;
    setAutoFillEnabled(false);
    const firstPassenger = purchase.passengers?.[0] || {};
    const currentValues = purchaserFormValuesRef.current || {};
    Object.entries(AUTOFILLED_IDENTITY_FIELDS).forEach(([field, passengerKey]) => {
      // Email is not fiscal identity data for the tercero, so keeping it does not contaminate
      // `crearTercero`. When the tenant hides the email input (`!SHOW_PURCHASER_EMAIL_FIELD`)
      // there is nowhere to re-enter it, and selectIsPurchaserFormValid drops email errors in
      // that config — clearing it here would let the purchase advance with no email and no
      // confirmation mail.
      if (field === 'email' && !features.SHOW_PURCHASER_EMAIL_FIELD) return;
      const value = currentValues[field];
      if (value && value === firstPassenger[passengerKey]) {
        dispatch(change('purchaser', field, ''));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThirdPartyBilling]);

  /**
   * Handle the change event for the document type field.
   * If the new value is 'NIT', it dispatches an action to change the person type to '1'.
   * on the transfer form.
   *
   * @param {object} event - The event object.
   * @param {string} newValue - The new value of the document type field.
   */
  const handleDocumentTypeChange = (event, newValue) => {
    if (newValue === 'NIT') {
      dispatch(change('transfer', 'personType', '1'));
    }
  };

  const handleBusinessNameChange = useCallback(
    (event, newValue) => {
      if (!showBillingCompanyExperience || billingCompanyFound) return;
      dispatch(change('purchaser', 'taxpayerLegalName', newValue || ''));
    },
    [billingCompanyFound, dispatch, showBillingCompanyExperience],
  );

  const handleBillingCompanyCityChange = useCallback(
    (_event, newValue) => {
      const selectedCity = billingCompanyCities.find(
        ({ value }) => String(value) === String(newValue),
      );
      const cityName = selectedCity?.label || '';

      // The same pick feeds the buyer address, so there is a single city control in the form.
      dispatch(change('purchaser', 'addressAttributes.city', cityName));

      // A resolved company keeps the address the provider returned for it; only a manual
      // creation derives the company address from the selected city.
      if (billingCompanyFound) return;

      dispatch(change('purchaser', 'taxpayerAddress', cityName));
    },
    [billingCompanyCities, billingCompanyFound, dispatch],
  );

  // A stored purchaser can arrive with an address city but no catalog id (the catalog loads
  // later, and purchases made before this flow never had one). Match by name so the reused
  // city select is not blank while the address already holds a value.
  useEffect(() => {
    if (!showsAddressCity || !billingCompanyCities.length) return;

    const values = purchaserFormValuesRef.current || {};
    if (values.billingCompanyCityId) return;

    const storedCity = values.addressAttributes?.city;
    if (!storedCity) return;

    const match = billingCompanyCities.find(
      ({ label }) => label.trim().toLowerCase() === String(storedCity).trim().toLowerCase(),
    );
    if (!match) return;

    dispatch(change('purchaser', 'billingCompanyCityId', match.value));
    // Mirror what picking the city by hand would have done, so an alta that never touched the
    // selector still ships a company address. `found` never reaches here: it hides the section.
    if (!values.taxpayerAddress) {
      dispatch(change('purchaser', 'taxpayerAddress', match.label));
    }
  }, [billingCompanyCities, dispatch, showsAddressCity]);

  /**
   * Renders the document type and document ID fields for the purchaser.
   * The document type field is populated with options based on the purchaser's nationality
   * and the available document types. The document ID field is a text input field.
   * Both fields are conditionally disabled based on the purchaser's form state.
   *
   * @returns {JSX.Element} The rendered document type and document ID fields.
   */
  const renderDocumentField = () => {
    const docTypeRegex = getDocTypeRegex('purchaser', effectivePurchaserDocumentType);
    return (
      <Spacing size="S" isResponsive flexGrow>
        <DocumentTypeField
          name="documentType"
          validate={required}
          target="purchaser"
          dataValueRef="documentId"
          isDisabled={isDisabled('documentType')}
          onChange={handleDocumentTypeChange}
        />
        <Spacing size="S" vertical>
          <Field
            autoComplete="off"
            name="documentId"
            id="documentId"
            type="text"
            placeholder={t('document_number', { context: brand })}
            normalize={(input) => normalizeRegex(docTypeRegex, input)}
            component={fieldRenderer}
            isDisabled={isDisabled('documentId')}
            // The lookup is silent: its only visible trace is the spinner inside this input.
            isLoading={billingCompanyStatus === BILLING_COMPANY_STATUS.SEARCHING}
            validate={documentId}
          />
          {getDocumentTypeInstructioni18NKey(effectivePurchaserDocumentType) ? (
            <Text size="XS" color="accent">
              {t(getDocumentTypeInstructioni18NKey(effectivePurchaserDocumentType))}
            </Text>
          ) : null}
        </Spacing>
      </Spacing>
    );
  };

  // Billing a third party requires the buyer to differ from the passenger, so "use first
  // passenger data" is hidden in that case.
  const showSwitch =
    features.SHOW_PASSENGER_IS_BUYER && showPurchaserForm && !isExchange && !isThirdPartyBilling;
  // `usesBillingCompanyFields` already subsumes the legacy NIT check this form used to do
  // inline: with an empty billing-company allowlist (every tenant that has not opted into that
  // flow, Fospina included) `isBillingCompanyDocumentOrLegacy` falls back to
  // `documentType === 'NIT'`, trimmed and case-insensitive.
  const showBusinessNameLabel = acceptBusinessName && usesBillingCompanyFields;
  // The city catalog only exists for the company experience; without it the address city stays
  // a plain text input, which is what every other tenant keeps seeing.
  const billingCompanyCityOptions = showBillingCompanyExperience ? billingCompanyCities : [];
  return (
    <>
      <div className="passengers-form">
        <Spacing flexGrow vertical>
          {!hideIdentityFields &&
            showSwitch &&
            !walletTypeNeedsUserInPurchaser(selectedLoyaltyProgram) && (
              <LabelSwitch
                checked={autofillEnabled}
                onChange={toggleAutofill}
                id="check-passengers"
                label={t('is_the_buyer_a_passenger_checkout')}
              />
            )}
          {!hideIdentityFields && showDocumentField && acceptBusinessName && renderDocumentField()}
          {!hideIdentityFields && (
            <Spacing size="S" flexGrow isResponsive>
              <Field
                autoComplete={purchaserAutoComplete(
                  showBusinessNameLabel ? 'organization' : 'given-name',
                )}
                type="text"
                name="purchaserFirstName"
                id="purchaserFirstName"
                placeholder={showBusinessNameLabel ? t('business_name') : t('fore_name')}
                component={fieldRenderer}
                normalize={normalizeFirstName}
                isDisabled={isDisabled('purchaserFirstName')}
                onChange={handleBusinessNameChange}
                maxLength={features.PURCHASER_NAME_MAX_LENGTH}
              />
              {!showBusinessNameLabel && (
                <Field
                  autoComplete={purchaserAutoComplete('family-name')}
                  type="text"
                  name="purchaserLastName"
                  id="purchaserLastName"
                  placeholder={t('family_name_one')}
                  component={fieldRenderer}
                  normalize={normalizeName}
                  isDisabled={isDisabled('purchaserLastName')}
                  maxLength={features.PURCHASER_LAST_NAME_MAX_LENGTH}
                />
              )}
              {features.SHOW_PURCHASER_SECOND_LAST_NAME_FIELD && !showBusinessNameLabel && (
                <Field
                  // No standard autocomplete token exists for a second surname, so this
                  // field stays opted out rather than borrowing `family-name`.
                  autoComplete="off"
                  type="text"
                  name="purchaserSecondLastName"
                  id="purchaserSecondLastName"
                  placeholder={`${t('family_name_two')} ${t('optional')}`}
                  component={fieldRenderer}
                  isDisabled={isDisabled('purchaserSecondLastName')}
                  maxLength={features.PURCHASER_SECOND_LAST_NAME_MAX_LENGTH}
                />
              )}
            </Spacing>
          )}
          {/* Fallback only: tenants without the address section still need to pick a city to
              create the company. When the section renders, the city lives there instead. */}
          {showManualBillingCompany && !showsAddressCity && (
            <Field
              id="billingCompanyCityId"
              name="billingCompanyCityId"
              label={t('payment:label.city')}
              placeholder={t('payment:label.city')}
              options={billingCompanyCityOptions}
              noOptionsText={t('general:no_options_found')}
              component={FilterableSelectRenderer}
              onChange={handleBillingCompanyCityChange}
              validate={required}
            />
          )}
          <Spacing size="S" isResponsive flexGrow>
            <PhoneNumberField
              autoComplete={purchaserAutoComplete('tel-national')}
              isDisabled={isDisabled('phone')}
              phoneCountryFieldName="phoneCountry"
              phoneCodeFieldName="phoneCode"
              phoneFieldName="phone"
              formName="purchaser"
              placeholder={phoneNumberLabel}
              cls="form-item-wrap-l"
              initCountry={phoneCountry}
            />
            {features.SHOW_PURCHASER_EMAIL_FIELD && (
              <Field
                autoComplete={purchaserAutoComplete('email')}
                id="email"
                type="email"
                name="email"
                placeholder={t('email')}
                component={fieldRenderer}
                isDisabled={isDisabled('email')}
                maxLength={features.PURCHASER_EMAIL_MAX_LENGTH}
              />
            )}
          </Spacing>
          {!hideIdentityFields && showDocumentField && !acceptBusinessName && renderDocumentField()}
          {!hideIdentityFields && showAddressFields && (
            <AddressFields
              minimal={isThirdPartyBilling}
              cityOptions={billingCompanyCityOptions}
              cityFieldName="billingCompanyCityId"
              onCityChange={handleBillingCompanyCityChange}
            />
          )}
          {Boolean(others && others.purchaserNotice) && (
            <Text size="S">{others.purchaserNotice}</Text>
          )}
        </Spacing>
      </div>
    </>
  );
};

PurchaserFields.propTypes = {
  purchaser: PropTypes.object,
  isExchange: PropTypes.bool,
  initialValues: PropTypes.shape({
    phoneCountry: PropTypes.string,
  }),
  showPurchaserForm: PropTypes.bool,
  hideIdentityFields: PropTypes.bool,
  isQuickCheckout: PropTypes.bool,
  initialBillingCompany: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.string,
    document: PropTypes.string,
  }),
};

PurchaserFields.defaultProps = {
  isQuickCheckout: false,
  initialBillingCompany: null,
};

export default PurchaserFields;
